import { ReactElement, useEffect } from 'react';
import { Location, useLocation } from 'react-router-dom';

import { BizTargetType } from '@/constants/bizTargetType';
import { BizType } from '@/constants/bizType';
import { TiaraPageKey } from '@/constants/tiara';
import { getAuthStore } from '@/store/auth';
import tracker, { PageMeta } from '@/utils/EventTracker';

type GetTiaraPageKeyByLocation = (location: Location<any>) => TiaraPageKey | undefined;

type TiaraOptionsPageKeyObj<T> =
  | T
  | Partial<Record<keyof typeof BizType, T>>
  | Partial<Record<keyof typeof BizTargetType, T>>
  | Partial<Record<keyof typeof BizType, Partial<Record<keyof typeof BizTargetType, T>>>>;

type TiaraOptionsPageKey = TiaraPageKey | GetTiaraPageKeyByLocation | TiaraOptionsPageKeyObj<TiaraPageKey>;

type TiaraOptions<TProps> = {
  /** Tiara page mapping key, 우선순위는 타입 선언된 순서와 동일 */
  pageKey: TiaraOptionsPageKey;
  pageMeta?: PageMeta | (() => PageMeta);
  /** tracking 비활성화  */
  disabled?: boolean | ((props: TProps) => boolean);
};

export const getKeyValue = <T extends string>(pageKey: TiaraOptionsPageKeyObj<T>) => {
  const { bizType, bizTargetType } = getAuthStore();
  if (typeof pageKey === 'string') return pageKey;
  if (!bizType || !bizTargetType) return undefined;

  // bizType 하위에 bizTargetType 추가
  if (Object.hasOwn(pageKey, bizType)) {
    const bizTypeMap = pageKey as Partial<
      Record<keyof typeof BizType, T | Partial<Record<keyof typeof BizTargetType, T>>>
    >;
    const keyOrMap = bizTypeMap[bizType];
    if (!keyOrMap) return undefined;
    if (typeof keyOrMap === 'string') return keyOrMap;
    return keyOrMap[bizTargetType];
  }
  if (Object.hasOwn(pageKey, bizTargetType)) return (pageKey as Record<keyof typeof BizTargetType, T>)[bizTargetType];
  return undefined;
};

const usePageKey = (pageKey: TiaraOptionsPageKey) => {
  const location = useLocation();

  if (typeof pageKey === 'function') return pageKey(location);
  return getKeyValue<TiaraPageKey>(pageKey);
};

export const withTiara = <TProps extends object>(
  Component: (props: TProps) => ReactElement | null,
  options: TiaraOptions<TProps>
) => {
  // eslint-disable-next-line react/display-name
  return (props: TProps) => {
    const pageKey = usePageKey(options.pageKey);
    const isDisabled = typeof options.disabled === 'boolean' ? options.disabled : options.disabled?.(props);

    const currentPageMeta = typeof options.pageMeta === 'function' ? options.pageMeta() : options.pageMeta;

    useEffect(() => {
      if (!isDisabled && pageKey) {
        tracker.setPage(pageKey).setSection(pageKey).trackPage({ pageKey }).pageMeta(currentPageMeta).track();
      }
    }, [isDisabled, pageKey, currentPageMeta]);

    return <Component {...props} />;
  };
};
